/** images */
import React, { useState, useEffect, useContext } from 'react';

import cookie from 'react-cookies';
import { NotificationManager } from 'react-notifications';
import { useNavigate } from 'react-router-dom';

/** styles */
import './Home.css';

import cityImg from '../assets/images/amazon/city.svg';
import PdfIcon from '../assets/images/pdf-icon.svg';
import BnplFormContainer from '../components/Bnpl/BnplFormContainer';
import CreditLineDashboard from '../components/CreditLine/CreditLineDashboard';
import { SafeHtml } from '../components/FormElements';
import LoanDashboard from '../components/LoanDashboard';

/** components */
import Layout from '../components/common/Layout';
import OfferDetail from '../components/common/OfferDetail';
import TenantConfig from '../config';

/** constants */
import {
  APPLICATION_STATUS,
  doesApplicationHaveWIPSections,
  PARTNER_STATUS,
} from '../constants/status';

/** context */
import { UserContext } from '../context/UserContext';
import {
  getApplicationId,
  isPrimaryApplicant,
  isProductALoan,
  productName,
} from '../helpers/applicationHelp';
import { openFile } from '../helpers/file';

/** helper */
import { isCurrentDateInDateRange } from '../helpers/format';

/** pages */
import { getLoanProductInfo } from '../pages/cavenuesgrant/Landing';

/** service */
import { getApplications, getLoanProducts } from '../service/api';
import { getLoans } from '../service/loan';

const tenantName = TenantConfig.getTenant();

const Dashboard = () => {
  const [loadingLoans, setLoadingLoans] = useState(true);
  const [loadingApplications, setLoadingApplications] = useState(true);
  const [loans, setLoans] = useState([]);
  const navigate = useNavigate();
  const [instances, setInstances] = useState([]);
  const [wipApplications, setwWipApplications] = useState([]);
  const [showApplicationsHeader, setShowApplicationsHeader] = useState(false);
  const [loanOffer, setLoanOffer] = useState([]);
  const [isOfferOpen, setIsOfferOpen] = useState(false);
  const { profile, updateAppData, loading, setLoading } = useContext(UserContext);
  const [isLoan, setIsLoan] = useState(false);
  const [loanProducts, setLoanProducts] = useState('');
  const allLoadingAreCompleted = !loading && !loadingApplications && !loadingLoans;

  useEffect(() => {
    async function init() {
      try {
        setLoading(true);
        try {
          const loanProducts = await getLoanProducts();
          if (loanProducts?.data) {
            setLoanProducts(loanProducts?.data);
            setLoanOffer(
              loanProducts.data.filter((loan) => {
                return isCurrentDateInDateRange(loan.startDate, loan.endDate);
              })
            );
          }
        } catch (getLoanProdErr) {
          console.log(getLoanProdErr);
        }
        const res = await getApplications();
        setLoadingApplications(false);
        if (res && res.data) {
          setInstances(res.data.data);
          setwWipApplications(res.data.data);
          res.data.data.map((instance, index) => {
            const applicationStatusDetails = applicationStatusMap(instance);
            if (applicationStatusDetails) setShowApplicationsHeader(true);
          });
          setLoading(false);
        }
        setLoading(false);
      } catch (err) {
        console.log(err);
        if (profile?.email) {
          NotificationManager.warning(
            'There was a problem loading your applications',
            'Warning',
            10000
          );
          setLoading(false);
        } else {
          navigate('/landing');
        }
      }
    }

    init();
    // eslint-disable-next-line
  }, [navigate]);

  useEffect(() => {
    async function getProductInfo() {
      const loanProduct = await getLoanProductInfo();
      setIsLoan(isProductALoan(loanProduct));
      const isProgramOpen = isCurrentDateInDateRange(loanProduct.startDate, loanProduct.endDate);
      setIsOfferOpen(isProgramOpen);
    }

    getProductInfo();
  }, []);

  useEffect(() => {
    const loadLoans = async () => {
      try {
        const {
          data: { data },
        } = await getLoans();

        setLoans(data);
      } catch (e) {
        console.log(e);
      } finally {
        setLoadingLoans(false);
      }
    };
    loadLoans();
  }, []);

  const handleOpenApplication = async (instance) => {
    cookie.save('appId', instance.id, { path: '/' });
    updateAppData(instance);
    navigate(`/product/${instance.loanProductId}/application/${instance.id}`);
  };

  const downloadApprovalLetter = (approvedLetterProp) => {
    openFile(
      approvedLetterProp.value.fileId,
      approvedLetterProp.value.fileType,
      approvedLetterProp.value.fileType,
      true
    );
  };
  const showLetterIfNeeded = (instance) => {
    const approvedLetterProp = instance?.applicationFeatures?.find(
      (feature) => feature.itemType === 'ApprovalLetterProperty'
    );
    if (approvedLetterProp?.value) {
      return (
        <div>
          <a
            href="#"
            onClick={() => {
              downloadApprovalLetter(approvedLetterProp);
            }}
          >
            <img src={PdfIcon} width="25px" alt="file" />
            <span>Download pre-approval letter</span>
          </a>
        </div>
      );
    }
  };

  const handleApplyProgramClick = (loanProductId) => {
    // cookie.save('appId', instance.id, { path: '/' });
    localStorage.setItem('loanProductId', loanProductId);
    navigate(`/apply`);
  };

  const overrideApplicationStatus = (app) => {
    let applicationtStatus = app.status;
    const approvedRateProperty = app.applicationFeatures.find(
      (feature) => feature.itemType === 'ApprovedRateProperty'
    );

    let lAmoutApproved = approvedRateProperty?.value?.finalApprovedAmount;
    if (!lAmoutApproved) {
      lAmoutApproved = 0;
    }
    if (APPLICATION_STATUS.MANUAL_REVIEW_REQUIRED_SALES === applicationtStatus) {
      //if the app has item pending we display the DOCS message
      if (
        TenantConfig.general.checkApplicationPhases == true &&
        doesApplicationHaveWIPSections(app)
      ) {
        applicationtStatus = APPLICATION_STATUS.MANUAL_REVIEW_DOCS_REQUIRED_SALES;
      }
    } else if (APPLICATION_STATUS.CLOSED === applicationtStatus) {
      if (lAmoutApproved != 0) {
        applicationtStatus = 'FUNDED';
      }
    }
    return applicationtStatus;
  };
  const applicationStatusMap = (instance) => {
    let applicationtStatus = overrideApplicationStatus(instance);
    const statusMap = TenantConfig?.dashboard?.dashboardStatusMap[applicationtStatus];
    switch (applicationtStatus) {
      case APPLICATION_STATUS.BORROWER_AGREEMENT_ACCEPTED:
      case APPLICATION_STATUS.AUTOMATED_REVIEW_APPROVED:
      case APPLICATION_STATUS.MANUAL_REVIEW_APPROVED:
        switch (instance.partnerStatus) {
          case PARTNER_STATUS.PARTNER_VERIFICATION_REQUIRED:
            if (getTenant() === 'lightrx') {
              return {
                action: 'Contiune',
                summary: 'Your application has been approved!',
              };
            } else {
              return {
                action: 'Lock Account',
                summary: 'Waiting on account lock with product partner.',
              };
            }
          case PARTNER_STATUS.PARTNER_VERIFICATION_FAILED:
            return {
              action: 'Lock Account',
              summary: 'Partner lock failed try again or contact product partner.',
            };
        }
        // we look if there is a ContractDocumentProperty
        // and if all signers have signed else we show nb of pending signature
        const contractProp = instance.applicationFeatures.find(
          (feature) => feature.itemType === 'ContractDocumentProperty'
        );
        if (contractProp?.value?.signers && contractProp.value?.signers?.length > 1) {
          const pendingSignature = contractProp.value.signers?.filter((signature) => {
            return signature.status !== 'completed';
          });
          if (pendingSignature?.length > 0) {
            return {
              action: '',
              summary:
                pendingSignature.length +
                ' out of ' +
                contractProp.value.signers?.length +
                ' signer(s) are yet to sign the contract before this loan can be funded.',
              disable: true,
            };
          }
        }
        if (!statusMap) {
          return null;
        } else {
          return {
            action: statusMap?.buttonText,
            summary: statusMap?.summary,
            disable: statusMap?.disabled,
          };
        }
      default:
        if (!statusMap) {
          return null;
        } else {
          if (statusMap?.buttonText === 'hidden') {
            return null;
          } else {
            return {
              action: statusMap ? statusMap?.buttonText : 'Continue',
              disable: statusMap ? !!statusMap.disabled : true,
              summary: statusMap ? statusMap.summary : 'Your application is being reviewed.',
            };
          }
        }
    }
  };

  const renderApplicationDescription = (app) => {
    let applicationDescription;
    if (isLoan) {
      applicationDescription = TenantConfig?.dashboard.applicationStatusPrefix + ' ';
      applicationDescription = applicationDescription + '' + getApplicationId(app, true);
    } else {
      if (tenantName == 'lacountyeog') {
        applicationDescription =
          loanProducts?.find((l) => app.loanProductId === l.id)?.productName +
          ' Application for ' +
          (app.applicationFeatures[1].value?.companyName
            ? (app.applicationFeatures[1].value?.companyName).substring(0, 25) + ' -- '
            : '') +
          '(Your Application ID: ' +
          getApplicationId(app, true) +
          ')';
      } else {
        applicationDescription =
          TenantConfig?.dashboard.applicationStatusPrefix +
          ' ' +
          (tenantName === 'canppa' && app.applicationFeatures[2].format?.fiscalSponseeName
            ? app.applicationFeatures[2].value?.fiscalSponseeName
              ? app.applicationFeatures[2].value?.fiscalSponseeName + ' '
              : ''
            : app.applicationFeatures[1].value?.companyName
            ? app.applicationFeatures[1].value?.companyName + ' '
            : '') +
          ((tenantName === 'ici' || tenantName === 'bhcipcce') &&
          app.applicationFeatures[0].value?.projectTitle
            ? app.applicationFeatures[0].value?.projectTitle + ' -- '
            : '');
        applicationDescription = applicationDescription + '(' + getApplicationId(app, true) + ')';
      }
    }
    return applicationDescription;
  };

  const renderContent = () => {
    return (
      allLoadingAreCompleted && (
        <div className={TenantConfig?.dashboard.mainClassName}>
          {showApplicationsHeader && (
            <div className="dashboard-header">
              <h1 className="title">Applications</h1>
            </div>
          )}
          {tenantName !== 'bhcipcce' &&
            tenantName !== 'ici' &&
            tenantName !== 'lacountyeog' &&
            tenantName !== 'canppa' &&
            tenantName !== 'cavenuesgrant' &&
            isOfferOpen &&
            instances.length == 0 &&
            wipApplications.length == 0 && (
              <>
                <div className="d-flex application">
                  <div className="d-flex">
                    <div className="main-text">
                      <h3>You don’t have an application yet.</h3>
                    </div>
                  </div>
                  <div className="warning-icon">
                    <button className="primary-btn" onClick={() => navigate('/apply')}>
                      Apply now
                    </button>
                  </div>
                </div>
              </>
            )}
          {!!wipApplications.length &&
            wipApplications.map((instance, index) => {
              const applicationStatusDetails = applicationStatusMap(instance);
              if (applicationStatusDetails) {
                const buttonShouldBeHidden = applicationStatusDetails?.disable
                  ? { display: 'none' }
                  : { display: 'block' };
                return (
                  <React.Fragment key={`app-${index}`}>
                    <div className="d-flex application">
                      <div className="d-flex">
                        <div className="main-text">
                          <h3 className="d-flex">
                            {renderApplicationDescription(instance)}
                            <OfferDetail features={instance?.applicationFeatures} />
                          </h3>
                          <p>
                            <b>
                              {!isPrimaryApplicant(instance) && 'Co-borrower '} Application Status:
                            </b>{' '}
                            {applicationStatusDetails?.summary}
                            {showLetterIfNeeded(instance)}
                          </p>
                        </div>
                      </div>
                      <div className="warning-icon">
                        <button
                          style={buttonShouldBeHidden}
                          className="primary-btn"
                          onClick={() => handleOpenApplication(instance)}
                          disabled={applicationStatusDetails?.disable}
                        >
                          {applicationStatusDetails?.action}
                        </button>
                      </div>
                    </div>
                  </React.Fragment>
                );
              }
            })}
          {(tenantName === 'bhcipcce' ||
            tenantName === 'canppa' ||
            tenantName === 'lacountyeog' ||
            tenantName === 'ici' ||
            tenantName === 'cavenuesgrant' ||
            tenantName === 'illinoisb2b') &&
            loanOffer.length && (
              <>
                <div className="dashboard-header">
                  <h1 className="title">Programs available</h1>
                </div>

                {loanOffer.map((loanProduct, index) => (
                  <div className="d-flex application" key={`offer-btb-${index}`}>
                    <div className="d-flex">
                      <div className="main-text">
                        <div className="warning-icon">
                          <button
                            className="primary-btn"
                            onClick={() => handleApplyProgramClick(loanProduct.id)}
                          >
                            Apply for {loanProduct.productName}
                          </button>
                        </div>
                      </div>
                      {TenantConfig?.dashboard?.showLoanProductDescription === true && (
                        <div>
                          <SafeHtml originalHtml={loanProduct.productDescription} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          {!!loans.length &&
            TenantConfig?.dashboard.showLoanSelfService &&
            (loans.some((loan) => loan?.productType === 'SMB_CONTRACTOR') ? (
              <CreditLineDashboard loans={loans} />
            ) : (
              <LoanDashboard loans={loans} />
            ))}
          {TenantConfig?.dashboard.renderBottomImg &&
            (!loans.length || !TenantConfig?.dashboard.showLoanSelfService) && (
              <div className="bottom-section">
                <img src={cityImg} alt="" className="bottom-image" />
              </div>
            )}
        </div>
      )
    );
  };

  if (getTenant() === 'lightrx') {
    return (
      <BnplFormContainer isDesktop={true} hideBack={true}>
        {renderContent()}
      </BnplFormContainer>
    );
  }
  return <Layout>{renderContent()}</Layout>;
};

export default Dashboard;
